import { useEffect, useRef, type RefObject } from 'react';
import { APP_BAR_HEIGHT } from '../admin-theme';

/**
 * Height of the fixed app bar (CustomAppBar) plus a little
 * breathing room, so a list scrolled "to the top" isn't tucked under it.
 */
const APP_BAR_OFFSET = APP_BAR_HEIGHT + 8;

/** Nearest ancestor that actually scrolls (a dialog body, a capped panel), or null for the window. */
function scrollParentOf(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Bring the top of a list back into view after a page change.
 *
 * The admin scrolls the document (react-admin's <Layout> sets no overflow on its
 * content box; the app bar and sidebar are position:fixed), so for a page list
 * this scrolls the window. Inside a dialog or another scrolling box it scrolls
 * that box instead. It only ever scrolls UP: if the list's top edge is already
 * on screen (e.g. the pager sits right under the filters) nothing moves.
 * A list that owns its scrolling (a TableContainer with maxHeight, for a
 * stickyHeader) is also reset to its first row.
 *
 * `below` is a sticky header the list scrolls under (Product Management, Uber
 * Menu): the list then stops under that header's bottom edge, not the app bar's.
 */
export function scrollToListTop(el: HTMLElement | null | undefined, below?: HTMLElement | null): void {
  if (!el) return;
  if (el.scrollTop > 0) el.scrollTop = 0;
  const top = el.getBoundingClientRect().top;
  const parent = scrollParentOf(el);
  if (parent) {
    const parentTop = parent.getBoundingClientRect().top;
    if (top < parentTop) parent.scrollTop += top - parentTop;
    return;
  }
  const offset = below ? below.getBoundingClientRect().bottom + 8 : APP_BAR_OFFSET;
  if (top < offset) window.scrollBy({ top: top - offset, left: 0, behavior: 'auto' });
}

/**
 * Attach the returned ref to the list's container (the card/section that holds
 * the table and its header). Whenever `page` changes after the first render the
 * container's top is scrolled back into view, so clicking "page 2" at the bottom
 * of page 1 lands on the first row of page 2, not the bottom of it.
 *
 * `closest` resolves the target from the ref'd element instead, e.g.
 * '.RaList-root' when the ref sits on a react-admin pagination component.
 * `below` is passed through to scrollToListTop.
 */
export function useScrollToListTop<T extends HTMLElement = HTMLDivElement>(
  page: unknown,
  options: { closest?: string; below?: RefObject<HTMLElement | null> } = {},
) {
  const ref = useRef<T>(null);
  // The page last scrolled for, not a "first render" flag: StrictMode runs the
  // mount effect twice, and a boolean would let the second run scroll on mount.
  const prevPage = useRef(page);
  const { closest, below } = options;
  useEffect(() => {
    if (Object.is(prevPage.current, page)) return;
    prevPage.current = page;
    const el = ref.current;
    // No match for `closest` means no real target; don't fall back to the ref'd
    // node, which may be a display:contents wrapper with no box of its own.
    const target = closest ? el?.closest<HTMLElement>(closest) : el;
    scrollToListTop(target, below?.current);
  }, [page, closest, below]);
  return ref;
}

export default useScrollToListTop;
